import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, Crown, Award, MapPin } from "lucide-react";
import { TierChip } from "@/components/TierBadge";

const podium = [
  { icon: Crown, color: "bg-gradient-to-br from-[#E7B24A] to-[#B7871C] text-white", label: "1º" },
  { icon: Trophy, color: "bg-gradient-to-br from-[#C0C0C0] to-[#8E8E8E] text-white", label: "2º" },
  { icon: Award, color: "bg-gradient-to-br from-[#CD7F32] to-[#8B5A2B] text-white", label: "3º" },
];

export default function LeaderboardSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/leaderboard?limit=10").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const top3 = items.slice(0, 3);
  const rest = items.slice(3, 10);

  return (
    <section className="max-w-7xl mx-auto px-6 py-20" data-testid="public-leaderboard">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="eyebrow">Ranking · Top 10</div>
        <h2 className="font-heading text-4xl sm:text-5xl mt-3 text-[var(--ink)]">Os nossos melhores<br/>referenciadores</h2>
        <p className="text-[var(--ink-soft)] mt-4">Reconhecemos quem nos ajuda a construir a rede de cuidado da CARING. Junte-se a eles.</p>
      </div>

      {items.length === 0 ? (
        <div className="card-surface p-10 text-center max-w-2xl mx-auto">
          <p className="text-[var(--ink-soft)]">Seja o primeiro a entrar no ranking. Registe-se e comece já a recomendar.</p>
        </div>
      ) : (
        <>
          {/* Podium — top 3 */}
          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto stagger">
            {top3.map((r, i) => {
              const p = podium[i];
              const Icon = p.icon;
              return (
                <div
                  key={r.code}
                  className={`card-surface p-6 relative overflow-hidden ${i === 0 ? "md:transform md:-translate-y-3" : ""}`}
                  data-testid={`leaderboard-${i + 1}`}
                >
                  <div className={`w-14 h-14 rounded-2xl grid place-items-center ${p.color}`}>
                    <Icon className="w-6 h-6"/>
                  </div>
                  <div className="mt-4 eyebrow">{p.label} lugar</div>
                  <div className="font-heading text-3xl text-[var(--ink)] mt-1">{r.display_name}</div>
                  <div className="text-xs text-[var(--ink-soft)] mt-1 flex items-center gap-2 flex-wrap">
                    {r.locality && <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/> {r.locality}</span>}
                    {r.tier && <TierChip tier={r.tier} testId={`leaderboard-tier-${i + 1}`} />}
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-[var(--muted)]">
                      <div className="text-xs text-[var(--ink-soft)]">Contratações</div>
                      <div className="font-heading text-2xl text-[var(--forest)]">{r.hires}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-[rgba(217,108,74,0.1)]">
                      <div className="text-xs text-[var(--ink-soft)]">Recebido</div>
                      <div className="font-heading text-2xl text-[var(--terracotta)]">{Number(r.earned).toFixed(0)} €</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Positions 4–10 */}
          {rest.length > 0 && (
            <div className="mt-8 max-w-4xl mx-auto card-surface overflow-hidden" data-testid="leaderboard-rest">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--muted)] text-left">
                    <th className="px-4 py-3 w-16">Pos.</th>
                    <th className="px-4 py-3">Referenciador</th>
                    <th className="px-4 py-3 hidden sm:table-cell">Localidade</th>
                    <th className="px-4 py-3 text-right">Contratações</th>
                    <th className="px-4 py-3 text-right">Recebido</th>
                  </tr>
                </thead>
                <tbody>
                  {rest.map((r, i) => {
                    const pos = i + 4;
                    return (
                      <tr key={r.code} className="border-t border-[var(--border)]" data-testid={`leaderboard-${pos}`}>
                        <td className="px-4 py-3 font-heading text-lg text-[var(--ink-soft)]">#{pos}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{r.display_name}</div>
                          {r.tier?.label && r.tier.label !== "Sem emblema" && (
                            <div className="text-[10px] mt-0.5"><TierChip tier={r.tier}/></div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-[var(--ink-soft)] hidden sm:table-cell">{r.locality || "—"}</td>
                        <td className="px-4 py-3 text-right font-heading text-lg text-[var(--forest)]">{r.hires}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-[var(--terracotta)]">{Number(r.earned).toFixed(0)} €</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
